import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useFormat, usePrefs } from "../contexts/PrefsContext";
import { useCollection, CrudActions, Modal, PageHeader, Empty } from "../components/Crud";
import { Plus, Heart, Target, Repeat, Wallet, ChefHat, Calculator, Sparkles } from "lucide-react";
import ImageEditor from "../components/ImageEditor";
import VideoTrimmer from "../components/VideoTrimmer";
import HabitTracker from "../components/HabitTracker";
import { estimateTax } from "../lib/taxRules";
import { useTaxCountries } from "../lib/useTaxCountries";

const TABS_PRO = ["goals", "habits", "finance", "tax", "editor"];
const TABS_STU = ["goals", "habits", "finance", "tax", "meal planning", "recipes"];

export default function Personal() {
    const { t } = useTranslation();
    const { user, effectiveRole } = useAuth();
    // Honour founder "View As" mode: tabs mirror the effective role so a
    // founder previewing "student" sees Meal Planning + Recipes.
    const isStudent = (effectiveRole || user?.role) === "student";
    const tabs = isStudent ? TABS_STU : TABS_PRO;
    const location = useLocation();
    const navigate = useNavigate();
    const hashTab = decodeURIComponent((location.hash || "").replace(/^#/, "")).toLowerCase();
    const initialTab = tabs.includes(hashTab) ? hashTab : tabs[0];
    const [tab, setTab] = useState(initialTab);
    useEffect(() => {
        if (hashTab && tabs.includes(hashTab) && hashTab !== tab) setTab(hashTab);
    }, [location.hash, hashTab, tab, tabs]);
    const selectTab = (tt) => {
        setTab(tt);
        navigate(`#${encodeURIComponent(tt)}`, { replace: true });
    };
    return (
        <div data-testid="personal-page">
            <PageHeader title={t("nav.personal")} subtitle={t("pages.personalEyebrow")} />
            <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-thin">
                {tabs.map(t => (
                    <button key={t} onClick={() => selectTab(t)} data-testid={`personal-tab-${t.replace(/\s/g, "-")}`}
                            className={`px-4 py-1.5 rounded-full text-sm capitalize whitespace-nowrap ${
                                tab === t ? "brand-gradient text-white" : "btn-ghost"
                            }`}>{t}</button>
                ))}
            </div>
            {tab === "goals" && <Goals />}
            {tab === "habits" && <HabitTracker />}
            {tab === "finance" && <Finance isStudent={isStudent} />}
            {tab === "tax" && <TaxCalc />}
            {tab === "meal planning" && <MealPlanning />}
            {tab === "recipes" && <Recipes />}
            {tab === "editor" && <EditorTabs />}
        </div>
    );
}

function SoonCard({ icon: Icon, title, hint }) {
    return <div className="card-warm p-10 text-center" data-testid="soon-card"><Icon className="mx-auto text-[var(--brand-magenta)] mb-2" /><div className="font-serif text-xl">{title}</div><div className="text-sm text-[var(--text-ink)]/65 mt-1">{hint}</div></div>;
}

function EditorTabs() {
    const [sub, setSub] = useState("image");
    return (
        <div data-testid="editor-tabs">
            <div className="flex gap-2 mb-4">
                {[["image", "Image editor"], ["video", "Video editor"]].map(([k, label]) => (
                    <button key={k} onClick={() => setSub(k)} data-testid={`editor-sub-${k}`}
                            className={`text-sm px-3 py-1.5 rounded-full ${sub === k ? "brand-gradient text-white" : "btn-ghost"}`}>
                        {label}
                    </button>
                ))}
            </div>
            {sub === "image" ? <ImageEditor /> : <VideoTrimmer />}
        </div>
    );
}

function Goals() {
    const { items, create, update, remove } = useCollection("goals");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", note: "", image_url: "" });
    return (
        <div>
            <button onClick={() => setOpen(true)} data-testid="add-goal-btn" className="btn-primary mb-4 inline-flex items-center gap-2"><Target size={14}/> Add goal</button>
            {items.length === 0 ? <Empty icon={Target} title="Build your mood-board" hint="Pin goals, dreams, and inspiration." /> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(g => (
                        <div key={g.id} className="card-warm p-3 group" data-testid={`goal-card-${g.id}`}>
                            {g.image_url && <img src={g.image_url} alt="" className="w-full h-40 object-cover rounded-xl mb-3" />}
                            <div className="flex justify-between items-start">
                                <div className="flex-1"><div className="font-serif text-lg">{g.title}</div>
                                    <div className="text-xs text-[var(--text-ink)]/65">{g.note}</div></div>
                                <CrudActions idBase={`goal-${g.id}`} onEdit={() => { setForm(g); setOpen(true); }} onDelete={() => remove(g.id)} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <Modal open={open} onClose={() => { setOpen(false); setForm({ title: "", note: "", image_url: "" }); }} title="Add goal">
                <form onSubmit={(e) => { e.preventDefault(); (form.id ? update(form.id, form) : create(form)); setOpen(false); setForm({ title: "", note: "", image_url: "" }); }} className="grid gap-2">
                    <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="goal-input-title" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <textarea placeholder="Note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <input placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <button className="btn-primary" data-testid="goal-save-btn">Save</button>
                </form>
            </Modal>
        </div>
    );
}

function Habits() {
    const { items, create, update, remove } = useCollection("habits");
    const [name, setName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editVal, setEditVal] = useState("");
    const saveRename = async (h) => {
        const v = editVal.trim();
        setEditingId(null);
        if (v && v !== h.name) await update(h.id, { name: v });
    };
    const confirmDel = (h) => {
        if (window.confirm(`Delete habit "${h.name}"? This cannot be undone.`)) remove(h.id);
    };
    return (
        <div>
            <form onSubmit={(e) => { e.preventDefault(); if (name) { create({ name, streak: 0, log: [] }); setName(""); } }}
                  className="card-warm p-4 flex gap-2 mb-3">
                <Repeat size={18} className="self-center text-[var(--brand-purple)]" />
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New habit (e.g. read 20 min)" data-testid="habit-input"
                       className="flex-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <button className="btn-primary" data-testid="habit-add-btn">Add</button>
            </form>
            <div className="grid sm:grid-cols-2 gap-3">
                {items.map(h => (
                    <div key={h.id} className="card-warm p-4 flex items-center gap-3" data-testid={`habit-card-${h.id}`}>
                        <button onClick={() => update(h.id, { streak: (h.streak || 0) + 1 })} data-testid={`habit-bump-${h.id}`}
                                className="w-10 h-10 rounded-full brand-gradient text-white font-serif">+</button>
                        <div className="flex-1 min-w-0">
                            {editingId === h.id ? (
                                <input
                                    autoFocus
                                    value={editVal}
                                    onChange={(e) => setEditVal(e.target.value)}
                                    onBlur={() => saveRename(h)}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveRename(h); if (e.key === "Escape") setEditingId(null); }}
                                    data-testid={`habit-rename-input-${h.id}`}
                                    className="font-medium w-full px-2 py-1 rounded-lg border border-[var(--brand-purple)] bg-white/70"
                                />
                            ) : (
                                <button
                                    onClick={() => { setEditingId(h.id); setEditVal(h.name); }}
                                    data-testid={`habit-name-${h.id}`}
                                    title="Click to rename"
                                    className="font-medium text-left w-full truncate hover:text-[var(--brand-purple)] transition"
                                >{h.name}</button>
                            )}
                            <div className="text-xs text-[var(--text-ink)]/60">Streak: {h.streak || 0}</div>
                        </div>
                        <CrudActions idBase={`hab-${h.id}`} onDelete={() => confirmDel(h)} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function Finance({ isStudent }) {
    const { fmtCurrency, currency } = useFormat();
    const inc = useCollection("incomes");
    const exp = useCollection("expenses");
    const bud = useCollection("budgets");
    const loans = useCollection("loans");
    const totalIn = inc.items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
    const totalEx = exp.items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
    return (
        <div className="space-y-5" data-testid="finance-tab">
            <div className="grid sm:grid-cols-3 gap-3">
                <div className="card-warm p-4"><div className="text-xs uppercase">Income</div><div className="font-serif text-2xl" data-testid="finance-income-total">{fmtCurrency(totalIn)}</div></div>
                <div className="card-warm p-4"><div className="text-xs uppercase">Expenses</div><div className="font-serif text-2xl" data-testid="finance-expenses-total">{fmtCurrency(totalEx)}</div></div>
                <div className="card-warm p-4"><div className="text-xs uppercase">Balance</div><div className="font-serif text-2xl" data-testid="finance-balance-total">{fmtCurrency(totalIn - totalEx)}</div></div>
            </div>
            <FinanceList title="Income" coll={inc} fields={[["source", "Source"], ["amount", "Amount"], ["date", "Date"]]} type="number" />
            <FinanceList title="Expenses" coll={exp} fields={[["category", "Category"], ["amount", "Amount"], ["date", "Date"]]} />
            <FinanceList title="Budgets" coll={bud} fields={[["category", "Category"], ["limit", "Monthly limit"]]} />
            {isStudent && (
                <>
                    <FinanceList title="Student Loans" coll={loans} fields={[["term", "Term/Year"], ["amount", `Disbursement (${currency || "GBP"})`], ["date", "Date"]]} />
                    <div className="card-warm p-4 text-sm text-[var(--text-ink)]/70" data-testid="student-finance-note">
                        Track loan disbursements, scholarships, part-time income and set savings goals. Monthly summaries surface on the Dashboard.
                    </div>
                </>
            )}
        </div>
    );
}

function FinanceList({ title, coll, fields }) {
    const [form, setForm] = useState({});
    const [editing, setEditing] = useState(null); // full row while modal open
    const slug = title.toLowerCase().replace(/\s+/g, "-");
    const saveEdit = async () => {
        if (!editing) return;
        const patch = fields.reduce((acc, [k]) => ({ ...acc, [k]: editing[k] ?? "" }), {});
        // Preserve any existing status field (Paid/Unpaid) so payment-style
        // rows keep their status through an edit.
        if (Object.prototype.hasOwnProperty.call(editing, "status")) {
            patch.status = editing.status || "unpaid";
        }
        await coll.update(editing.id, patch);
        setEditing(null);
    };
    const hasStatus = editing && Object.prototype.hasOwnProperty.call(editing, "status");
    return (
        <div className="card-warm p-4">
            <div className="flex justify-between items-center mb-2">
                <div className="font-serif text-xl flex items-center gap-2"><Wallet size={16} /> {title}</div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); coll.create(form); setForm({}); }} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid={`${slug}-form`}>
                {fields.map(([k, l]) => (
                    <input key={k} placeholder={l} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                           data-testid={`${slug}-input-${k}`}
                           className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                ))}
                <button className="btn-primary text-sm"><Plus size={12} className="inline" /> Add</button>
            </form>
            <div className="space-y-1">
                {coll.items.map(r => (
                    <div key={r.id} className="flex items-center gap-2 text-sm py-1.5 border-b border-[rgba(26,19,24,0.06)] last:border-0" data-testid={`${slug}-row-${r.id}`}>
                        {fields.map(([k]) => <span key={k} className="flex-1 truncate">{r[k]}</span>)}
                        <CrudActions idBase={`fin-${r.id}`}
                                     onEdit={() => setEditing({ ...r })}
                                     onDelete={() => window.confirm(`Delete this ${title.toLowerCase()} entry?`) && coll.remove(r.id)} />
                    </div>
                ))}
            </div>
            <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit ${title.toLowerCase()}`}>
                {editing && (
                    <form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}
                          className="grid gap-3" data-testid={`${slug}-edit-form`}>
                        {fields.map(([k, l]) => (
                            <label key={k} className="text-sm">{l}
                                <input value={editing[k] ?? ""}
                                       onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                                       data-testid={`${slug}-edit-${k}`}
                                       className="mt-1 w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                            </label>
                        ))}
                        {hasStatus && (
                            <label className="text-sm">Status
                                <select value={editing.status || "unpaid"}
                                        onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                                        data-testid={`${slug}-edit-status`}
                                        className="mt-1 w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]">
                                    <option value="unpaid">Unpaid</option>
                                    <option value="paid">Paid</option>
                                </select>
                            </label>
                        )}
                        <div className="flex justify-end gap-2 mt-2">
                            <button type="button" className="btn-ghost" onClick={() => setEditing(null)} data-testid={`${slug}-edit-cancel`}>Cancel</button>
                            <button type="submit" className="btn-primary" data-testid={`${slug}-edit-save`}>Save changes</button>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}

function TaxCalc() {
    // Country-based personal-income tax ESTIMATE (v1). Uses the isolated
    // rules module in `lib/taxRules` so we can extend to VAT/GST, bands,
    // self-employed and regional rules later without touching this file.
    const { fmtCurrency } = useFormat();
    const { prefs, updatePrefs } = usePrefs();
    const [income, setIncome] = useState("");
    const country = prefs.tax_country || prefs.region || "GB";
    const { countries } = useTaxCountries();
    const result = estimateTax(country, parseFloat(income) || 0);
    const cur = result.country?.currency || prefs.currency || "GBP";
    const fmt = (n) => fmtCurrency(n, cur);

    return (
        <div className="card-warm p-5 sm:p-6 max-w-xl" data-testid="tax-calc">
            <div className="font-serif text-2xl flex items-center gap-2 mb-1">
                <Calculator size={18} /> Estimated Tax
            </div>
            <p className="text-xs text-[var(--text-ink)]/60 mb-4">
                This is an estimate only and should not be considered financial or tax advice.
            </p>

            <label className="text-sm block mb-3">
                Tax country
                <select value={country}
                        onChange={(e) => updatePrefs({ tax_country: e.target.value })}
                        data-testid="tax-country-select"
                        className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                    {countries.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                </select>
            </label>

            <label className="text-sm block">
                Annual income ({cur})
                <input value={income} onChange={(e) => setIncome(e.target.value)} type="number"
                       min="0"
                       data-testid="tax-input-income"
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
            </label>

            <div className="grid grid-cols-2 gap-3 mt-4 text-sm" data-testid="tax-breakdown">
                {result.personal_allowance > 0 && (
                    <>
                        <div>Personal allowance</div>
                        <div className="font-mono">{fmt(result.personal_allowance)}</div>
                    </>
                )}
                <div>Taxable</div><div className="font-mono">{fmt(result.taxable)}</div>
                {result.breakdown.map((b, i) => (
                    <React.Fragment key={i}>
                        <div>{b.label}</div>
                        <div className="font-mono">{fmt(b.amount)}</div>
                    </React.Fragment>
                ))}
                <div>Effective rate</div>
                <div className="font-mono">{(result.effective_rate * 100).toFixed(2)}%</div>
                <div className="font-serif text-lg" data-testid="tax-estimate-label">Estimated tax</div>
                <div className="font-serif text-lg" data-testid="tax-estimate-value">{fmt(result.tax)}</div>
            </div>

            {result.note && (
                <div className="mt-4 text-xs text-[var(--text-ink)]/55 border-t border-[rgba(26,19,24,0.08)] pt-3"
                     data-testid="tax-country-note">
                    {result.note}
                </div>
            )}
            {!result.supported && (
                <div className="mt-2 text-xs text-amber-700" data-testid="tax-unsupported-warning">
                    Country-specific rules aren&apos;t available yet — showing a generic 20% estimate.
                </div>
            )}
        </div>
    );
}

const MEAL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MEAL_TYPES = ["Breakfast", "Lunch", "Dinner", "Snack"];
const MEAL_VIEW_KEY = "vu_meal_view_v1";
const MEAL_VIEWS = [
    { key: "chronological", label: "By day",       icon: "📋" },
    { key: "type",          label: "By meal type", icon: "🍳" },
    { key: "grid",          label: "Grid",         icon: "📅" },
];

function MealPlanning() {
    const { items, create, remove } = useCollection("meal_plans");
    const [form, setForm] = useState({ day: "Monday", meal: "Breakfast", title: "" });
    const [view, setView] = useState(() => {
        if (typeof window === "undefined") return "chronological";
        return localStorage.getItem(MEAL_VIEW_KEY) || "chronological";
    });
    const setViewPersist = (v) => {
        setView(v);
        try { localStorage.setItem(MEAL_VIEW_KEY, v); } catch { /* SSR */ }
    };

    // Chronological sort: by day-of-week then by meal-of-day.
    const dayOrder = Object.fromEntries(MEAL_DAYS.map((d, i) => [d, i]));
    const mealOrder = Object.fromEntries(MEAL_TYPES.map((m, i) => [m, i]));
    const sortedByChron = [...items].sort((a, b) => {
        const da = dayOrder[a.day] ?? 99, db = dayOrder[b.day] ?? 99;
        if (da !== db) return da - db;
        return (mealOrder[a.meal] ?? 99) - (mealOrder[b.meal] ?? 99);
    });

    // Group helper (returns a Map so insertion order is stable across renders).
    const groupBy = (arr, keyFn, groupOrder) => {
        const map = new Map(groupOrder.map(k => [k, []]));
        for (const it of arr) {
            const k = keyFn(it);
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(it);
        }
        return [...map.entries()].filter(([, v]) => v.length > 0);
    };

    return (
        <div data-testid="meal-planning">
            <form onSubmit={(e) => { e.preventDefault(); create(form); setForm({ day: "Monday", meal: "Breakfast", title: "" }); }}
                  className="card-warm p-4 grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4" data-testid="meal-form">
                <select value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]">
                    {MEAL_DAYS.map(d => <option key={d}>{d}</option>)}
                </select>
                <select value={form.meal} onChange={(e) => setForm({ ...form, meal: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]">
                    {MEAL_TYPES.map(d => <option key={d}>{d}</option>)}
                </select>
                <input required placeholder="What's on the plate?" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <button className="btn-primary">Add</button>
            </form>

            {/* View selector — persisted across sessions */}
            <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="meal-view-switcher">
                <span className="text-xs text-[var(--text-ink)]/55 mr-1">View:</span>
                {MEAL_VIEWS.map(v => (
                    <button key={v.key}
                            type="button"
                            onClick={() => setViewPersist(v.key)}
                            data-testid={`meal-view-${v.key}`}
                            aria-pressed={view === v.key}
                            className={`text-xs px-2.5 py-1 rounded-full border transition ${
                                view === v.key
                                    ? "brand-gradient text-white border-transparent"
                                    : "border-[rgba(26,19,24,0.15)] text-[var(--text-ink)]/70 hover:bg-[rgba(255,107,53,0.06)]"
                            }`}>
                        <span aria-hidden="true" className="mr-1">{v.icon}</span>{v.label}
                    </button>
                ))}
            </div>

            {items.length === 0 && (
                <div className="card-warm p-6 text-sm text-[var(--text-ink)]/60" data-testid="meal-empty">
                    No meals planned yet. Use the form above to add breakfast, lunch, dinner or snacks.
                </div>
            )}

            {view === "chronological" && items.length > 0 && (
                <div className="space-y-4" data-testid="meal-view-chronological-list">
                    {groupBy(sortedByChron, m => m.day, MEAL_DAYS).map(([day, list]) => (
                        <div key={day} className="card-warm p-4" data-testid={`meal-day-${day}`}>
                            <div className="font-serif text-lg mb-2">{day}</div>
                            <ul className="space-y-1.5">
                                {list.sort((a, b) => (mealOrder[a.meal] ?? 99) - (mealOrder[b.meal] ?? 99)).map(m => (
                                    <MealRow key={m.id} m={m} remove={remove} showDay={false} showMeal />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {view === "type" && items.length > 0 && (
                <div className="space-y-4" data-testid="meal-view-type-list">
                    {groupBy(sortedByChron, m => m.meal, MEAL_TYPES).map(([meal, list]) => (
                        <div key={meal} className="card-warm p-4" data-testid={`meal-type-${meal}`}>
                            <div className="font-serif text-lg mb-2">{meal}</div>
                            <ul className="space-y-1.5">
                                {list.map(m => <MealRow key={m.id} m={m} remove={remove} showDay showMeal={false} />)}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {view === "grid" && items.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="meal-view-grid">
                    {items.map(m => (
                        <div key={m.id} className="card-warm p-3 flex items-center gap-3">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--brand-orange)]/15 text-[var(--brand-orange)]">{m.day}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--brand-purple)]/15 text-[var(--brand-purple)]">{m.meal}</span>
                            <div className="flex-1">{m.title}</div>
                            <CrudActions idBase={`mp-${m.id}`} onDelete={() => remove(m.id)} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function MealRow({ m, remove, showDay, showMeal }) {
    return (
        <li className="flex items-center gap-2 py-1" data-testid={`meal-row-${m.id}`}>
            {showDay && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand-orange)]/15 text-[var(--brand-orange)] shrink-0">{m.day}</span>}
            {showMeal && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] shrink-0 min-w-[64px] text-center">{m.meal}</span>}
            <span className="flex-1 text-sm">{m.title}</span>
            <CrudActions idBase={`mp-${m.id}`} onDelete={() => remove(m.id)} />
        </li>
    );
}

function Recipes() {
    const { items, create, update, remove } = useCollection("recipes");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", image_url: "", ingredients: "", steps: "", favorite: false });
    const submit = (e) => { e.preventDefault(); (form.id ? update(form.id, form) : create(form)); setOpen(false); setForm({ name: "", image_url: "", ingredients: "", steps: "", favorite: false }); };
    return (
        <div data-testid="recipes-tab">
            <button onClick={() => setOpen(true)} className="btn-primary mb-4 inline-flex items-center gap-2" data-testid="add-recipe-btn"><ChefHat size={14} /> Add recipe</button>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(r => (
                    <div key={r.id} className="card-warm overflow-hidden" data-testid={`recipe-card-${r.id}`}>
                        {r.image_url && <img src={r.image_url} alt="" className="w-full h-40 object-cover" />}
                        <div className="p-4">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="font-serif text-lg flex items-center gap-2">
                                        {r.favorite && <Heart size={14} className="text-[var(--brand-orange)]" fill="currentColor" />}
                                        {r.name}
                                    </div>
                                    <div className="text-xs text-[var(--text-ink)]/65 line-clamp-2">{r.ingredients}</div>
                                </div>
                                <CrudActions idBase={`rec-${r.id}`}
                                    isFav={r.favorite}
                                    onFavorite={() => update(r.id, { favorite: !r.favorite })}
                                    onEdit={() => { setForm(r); setOpen(true); }}
                                    onDelete={() => remove(r.id)} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit recipe" : "Add recipe"}>
                <form onSubmit={submit} className="grid gap-2">
                    <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="recipe-input-name" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <textarea rows={3} placeholder="Ingredients" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <textarea rows={4} placeholder="Steps" value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <button className="btn-primary" data-testid="recipe-save-btn">Save</button>
                </form>
            </Modal>
        </div>
    );
}
